import { fetch } from '../utils/request';

const getUrl = (path) => `${process.env.MONO_API}/graphics${path}`;

export default {
  /**
   * Get list of Saved Graphics for a production.
   * @param {Object} opts
   * @param {String} opts.productionId - The production ID to filter by.
   * @param {Number} opts.page - Page number
   * @returns {Promise<Array<Object>>} List of saved graphics.
   */
  async getGraphics(opts = {}) {
    const {
      productionId,
      page,
    } = opts;

    const query = {
      page,
      productionId,
    };

    return fetch(getUrl('/saved-graphics'), { query });
  },

  /**
   * Get list of graphic templates, paginated.
   * @param {Object} opts
   * @param {Number} opts.page - Page number
   * @param {Number} opts.limit - Amount of templates to fetch
   * @param {Array} opts.tags - Array of tags to filter by
   * @param {String} opts.search - Search term to filter by
   * @returns {Promise<Array<Object>>} List of graphic templates.
   */
  async getTemplates(opts = {}) {
    const {
      page = 1,
      limit = 20,
      tags = [],
      search = '',
    } = opts;

    return fetch(getUrl('/templates'), {
      query: {
        page,
        limit,
        tags: tags.join(','),
        search,
      },
    });
  },

  /**
   * Get list of unique tags across graphic templates for account.
   * @returns {Promise<Array<String>>} List of unique tags.
   */
  getTags() {
    return fetch(getUrl('/templates/tags'));
  },

  /**
   * Fetch a graphic template by ID.
   * @param {Number} id - The template ID.
   * @returns {Promise<Object>} The template object.
   */
  getTemplate(id) {
    return fetch(getUrl(`/templates/${id}`));
  },

  /**
   * Get list of templates by ID(s).
   * @param {Array} ids - Array of template IDs.
   * @returns {Promise<Array>} List of template objects.
   */
  getMultipleTemplates(ids = []) {
    return fetch(getUrl('/templates/many'), {
      query: {
        templates: ids.join(','),
      },
    });
  },

  /**
   * Create a new Saved Graphic.
   */
  createGraphic(fields = {}) {
    return fetch(getUrl('/saved-graphics'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fields),
    });
  },

  /**
   * Duplicate multiple saved graphics by production.
   * @param {String} productionId - The production ID to duplicate from.
   * @param {String} duplicateId - The production ID to duplicate to.
   * @returns {Promise}
   */
  duplicateSavedGraphics(productionId, duplicateId) {
    return fetch(getUrl(`/saved-graphics/productions/${productionId}/duplicate`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        duplicateId,
      }),
    });
  },

  /**
   * Update an existing saved graphic by ID.
   * @param {Number} id - The saved graphic ID.
   * @param {Object} fields - The fields to update.
   * @returns {Promise<Object>} The updated saved graphic object.
   */
  editGraphic(id, fields = {}) {
    return fetch(getUrl(`/saved-graphics/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fields),
    });
  },

  /**
   * Delete a Saved Graphic by ID.
   * @param {Number} id - The saved graphic ID.
   * @returns {Promise}
   */
  deleteGraphic(id) {
    return fetch(getUrl(`/saved-graphics/${id}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
};
